import { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import DropDown from '../../components/dropdown/DropDown';
import MultiSelectDropdown from '../../components/multiselectdropdown/MultiSelectDropdown';
import Input from '../../components/input/Input';
import theme from '../../styles/theme';
import {
  getCapitalContributions,
  getProtectorInstallmentConfrontations,
  exportProtectorInstallmentConfrontationsCsv,
  ProtectorConfrontationListOptions,
} from '../../services/GuaranteesService';
import { useConfig } from '../../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';
import { nextOverdueSortConfig } from '../../utils/overdueFilters';
import { buildProtectorConfrontationCsvFilename } from '../../utils/protectorConfrontationCsvFilename';
import { useToast } from '../../components/toast/toast';
import Icon from '../../components/icon/Icon';
import Tooltip from '../../components/tooltip/Tooltip';
import Spinner from '../../components/Spinner/spinner';

const TotalsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const TotalCardInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
`;

const TotalAmountsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
`;

const FiltersRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const FilterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  height: 48px;
  align-self: flex-end;
  box-sizing: border-box;

  button {
    background: none;
    border: none;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    cursor: pointer;
  }

  button:disabled {
    cursor: not-allowed;
  }
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const DROPDOWN_CONTROL_CSS = `
  width: 100%;
  height: 48px;

  & > div {
    width: 100%;
    height: 100%;
    margin: 0 !important;
  }

  & > div > div,
  & > div > div > div {
    margin-top: 0 !important;
    gap: 0 !important;
    height: 100%;
  }

  button {
    height: 48px !important;
    min-height: 48px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-left: ${theme.spacing['spacing-3']} !important;
    padding-right: ${theme.spacing['spacing-3']} !important;
    box-sizing: border-box;
  }
`;

const DropDownControl = styled.div`
  ${DROPDOWN_CONTROL_CSS}
`;

const InputControl = styled.div`
  width: 100%;
  height: 48px;

  & > div {
    width: 100%;
    height: 100%;
    margin: 0 !important;
  }

  & > div > div {
    margin-top: 0 !important;
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
    box-sizing: border-box;
    padding-left: ${theme.spacing['spacing-3']} !important;
    padding-right: ${theme.spacing['spacing-3']} !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    display: flex;
    align-items: center;
    overflow: visible;
    background-color: ${theme.colors.neutral.bg.inverted} !important;
    border-radius: ${theme.borderRadius['border-radius-3']} !important;
    box-shadow: inset 0 0 0 1px ${theme.colors.neutral.border.regular} !important;
  }

  input {
    height: 34px !important;
    max-height: 34px !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 100%;
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }
`;

const MultiSelectControl = styled.div`
  width: 100%;
  height: 48px;

  & > div {
    width: 100%;
    height: 100%;
  }

  & > div > div:first-child {
    min-height: 48px !important;
    height: 48px !important;
    box-sizing: border-box;
    padding: 0 ${theme.spacing['spacing-3']} !important;
  }
`;

const ClearFiltersLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.danger.text.weak};
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;

  &:hover:not(:disabled) {
    color: ${theme.colors.danger.text.regular};
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
  text-align: center;
  width: 100%;
`;

interface InstallmentConfrontation {
  id: number;
  confrontation_date: string;
  global_id: number;
  monetization_type_id: number;
  monetization_type_name: string;
  installment_id: number;
  installment_number: number;
  contribution_id: number | null;
  protector_id: number;
  protector_name: string;
  percentage: number;
  amount: number;
  currency_code: string | null;
  amount_ars: number | null;
  amount_usd: number | null;
  is_recovery: boolean;
  operation_type: 'CONFRONTED' | 'HONORED' | 'CANCELLED';
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface ConfrontationTotals {
  total_afrontado_ars: number | null;
  total_afrontado_usd: number | null;
  total_honrado_ars: number | null;
  total_honrado_usd: number | null;
  neto_ars: number | null;
  neto_usd: number | null;
}

interface ProtectorDetailsDefaultSectionProps {
  protectorId?: string;
  cuit?: string;
}

type ConfrontationSortConfig = {
  field: string;
  direction: 'asc' | 'desc';
};

const PAGE_SIZE = 20;
const AVAL_FILTER_DEBOUNCE_MS = 400;

const OPERATION_TYPE_OPTIONS = [
  { label: 'Todas', value: '' },
  { label: 'Afronte', value: 'CONFRONTED' },
  { label: 'Honra', value: 'HONORED' },
];

const MOVEMENT_TYPE_OPTIONS = [
  { label: 'Cheques', value: 'CHEQUES' },
  { label: 'Pagarés', value: 'PAGARES' },
  { label: 'Préstamos', value: 'PRESTAMOS' },
  { label: 'Aval Comercial', value: 'AVAL COMERCIAL' },
];

const emptyTotals = (): ConfrontationTotals => ({
  total_afrontado_ars: 0,
  total_afrontado_usd: 0,
  total_honrado_ars: 0,
  total_honrado_usd: 0,
  neto_ars: 0,
  neto_usd: 0,
});

const ProtectorDetailsDefaultSection = ({
  protectorId,
  cuit = '',
}: ProtectorDetailsDefaultSectionProps) => {
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const [confrontations, setConfrontations] = useState<InstallmentConfrontation[]>([]);
  const [totals, setTotals] = useState<ConfrontationTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<ConfrontationSortConfig | null>(null);
  const ignoreSortEchoRef = useRef(false);
  const sortConfigRef = useRef(sortConfig);
  sortConfigRef.current = sortConfig;
  const [pagination, setPagination] = useState({
    page: 0,
    size: PAGE_SIZE,
    totalElements: 0,
    totalPages: 0,
  });

  const [operationType, setOperationType] = useState('');
  const [globalIdInput, setGlobalIdInput] = useState('');
  const [globalIdContains, setGlobalIdContains] = useState('');
  const [monetizationTypeNames, setMonetizationTypeNames] = useState<string[]>([]);
  const [contributionIds, setContributionIds] = useState<string[]>([]);
  const [contributionOptions, setContributionOptions] = useState<
    Array<{
      label: string;
      value: string;
      badge?: { label: string; variant: 'success' | 'neutral' };
    }>
  >([]);

  const hasActiveFilters =
    Boolean(operationType) ||
    Boolean(globalIdInput.trim()) ||
    monetizationTypeNames.length > 0 ||
    contributionIds.length > 0;

  const isDirty = hasActiveFilters || sortConfig !== null;

  const buildFilters = useCallback((): ProtectorConfrontationListOptions => {
    return {
      contributionIds: contributionIds.length
        ? contributionIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
        : undefined,
      operationType: operationType || undefined,
      monetizationTypeNames: monetizationTypeNames.length ? monetizationTypeNames : undefined,
      globalIdContains: globalIdContains.trim() || undefined,
    };
  }, [contributionIds, operationType, monetizationTypeNames, globalIdContains]);

  const buildListOptions = useCallback((): ProtectorConfrontationListOptions => {
    const filters = buildFilters();
    if (!sortConfig) {
      return filters;
    }
    return {
      ...filters,
      sortBy: sortConfig.field,
      sortDirection: sortConfig.direction,
    };
  }, [buildFilters, sortConfig]);

  const fetchConfrontations = useCallback(
    async (page: number) => {
      if (!protectorId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await getProtectorInstallmentConfrontations(
          protectorId,
          page,
          PAGE_SIZE,
          buildListOptions()
        );
        if (response.success && 'data' in response) {
          const content = response.data.content || [];
          const totalElements = response.data.total_elements || 0;
          setConfrontations(content);
          if (totalElements === 0) {
            setTotals(emptyTotals());
          } else {
            setTotals({
              total_afrontado_ars: response.data.total_afrontado_ars ?? 0,
              total_afrontado_usd: response.data.total_afrontado_usd ?? 0,
              total_honrado_ars: response.data.total_honrado_ars ?? 0,
              total_honrado_usd: response.data.total_honrado_usd ?? 0,
              neto_ars: response.data.neto_ars ?? 0,
              neto_usd: response.data.neto_usd ?? 0,
            });
          }
          setPagination({
            page: response.data.page || 0,
            size: response.data.size || PAGE_SIZE,
            totalElements,
            totalPages: response.data.total_pages || 0,
          });
        } else {
          setError('Error al cargar los movimientos de afrontes y honras');
        }
      } catch (err) {
        setError('Error al cargar los movimientos de afrontes y honras');
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [protectorId, buildListOptions]
  );

  useEffect(() => {
    if (!protectorId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getCapitalContributions(protectorId, 0, 500);
        if (cancelled) return;
        if (response.success && 'data' in response) {
          const rows = response.data.content || response.data || [];
          const options = (Array.isArray(rows) ? rows : [])
            .filter((row: any) => row.enabled !== false)
            .map((row: any) => {
              const id = row.id ?? row.contribution_id;
              const withdrawn = Boolean(row.withdrawn_at);
              return {
                value: String(id),
                label: String(id),
                badge: {
                  label: withdrawn ? 'PASIVO' : 'ACTIVO',
                  variant: (withdrawn ? 'neutral' : 'success') as 'success' | 'neutral',
                },
              };
            });
          setContributionOptions(options);
        }
      } catch (err) {
        console.error('Error loading contribution filter options', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [protectorId]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = globalIdInput.trim();
      setGlobalIdContains((prev) => (prev === next ? prev : next));
    }, AVAL_FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [globalIdInput]);

  useEffect(() => {
    setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [globalIdContains]);

  useEffect(() => {
    if (protectorId) {
      fetchConfrontations(pagination.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protectorId, pagination.page, operationType, monetizationTypeNames, contributionIds, globalIdContains, sortConfig]);

  const handleSort = (field: string, _rdtDirection?: 'asc' | 'desc') => {
    if (ignoreSortEchoRef.current) return;

    const current = sortConfigRef.current ?? { field: null, direction: null };
    const config = nextOverdueSortConfig(current, field);
    if (!config.field || !config.direction) return;

    ignoreSortEchoRef.current = true;
    setSortConfig({ field: config.field, direction: config.direction });
    setPagination((prev) => ({ ...prev, page: 0 }));
    window.setTimeout(() => {
      ignoreSortEchoRef.current = false;
    }, 0);
  };

  const handleChangePage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const resetToFirstPage = () => {
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setOperationType('');
    setGlobalIdInput('');
    setGlobalIdContains('');
    setMonetizationTypeNames([]);
    setContributionIds([]);
    setSortConfig(null);
    resetToFirstPage();
  };

  const handleExportCsv = async () => {
    if (!protectorId || pagination.totalElements === 0) return;
    setIsExporting(true);
    try {
      const result = await exportProtectorInstallmentConfrontationsCsv(
        protectorId,
        buildListOptions(),
        localCurrencyCode,
      );
      if (!result?.success || !('data' in result)) {
        useToast.error({
          title:
            result?.status === 422
              ? 'No se pudo exportar: el filtro tiene demasiados movimientos. Acotá los filtros.'
              : 'Error al descargar el CSV.',
          id: 'confrontation-export',
          autoClose: 5000,
        });
        return;
      }
      const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = buildProtectorConfrontationCsvFilename(cuit, new Date());
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
    } finally {
      setIsExporting(false);
    }
  };

  const renderSortableHeader = (
    label: string,
    sortField: string,
    options?: { reserveSortGap?: boolean },
  ) => {
    const isActive = sortConfig?.field === sortField;
    const direction = isActive ? sortConfig?.direction : null;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          whiteSpace: 'nowrap',
          ...(options?.reserveSortGap ? { paddingRight: '6px' } : null),
        }}
      >
        <P variant='secondary-2' type="3" weight="bold" withMargin={false}>
          {label}
        </P>
        <span
          aria-hidden
          style={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: options?.reserveSortGap ? '12px' : undefined,
            fontSize: '10px',
            lineHeight: 1,
            color: isActive ? '#374151' : '#9CA3AF',
            opacity: isActive ? 1 : 0.45,
          }}
        >
          {isActive && direction === 'asc' ? '▲' : '▼'}
        </span>
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatDecimal = (amount: number) => {
    return amount.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatCardArs = (value: number | null) => {
    const amount = value ?? 0;
    const formatted = formatDecimal(Math.abs(amount));
    return amount < 0
      ? `- ${localCurrencyCode} ${formatted}`
      : `${localCurrencyCode} ${formatted}`;
  };

  const formatCardUsd = (value: number | null) => {
    const amount = value ?? 0;
    const formatted = formatDecimal(Math.abs(amount));
    return amount < 0 ? `- USD ${formatted}` : `USD ${formatted}`;
  };

  const formatPercentage = (percentage: number) => {
    if (percentage === null || percentage === undefined) {
      return '0%';
    }
    return `${percentage.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;
  };

  const formatHonoredRowArs = (amount: number | null, operationTypeValue: string) => {
    if (amount == null) return '-';
    const mag = Math.abs(amount);
    const prefix = operationTypeValue === 'HONORED' ? '- ' : '';
    return `${prefix}$ ${formatDecimal(mag)}`;
  };

  const formatHonoredRowUsd = (amount: number | null, operationTypeValue: string) => {
    if (amount == null) return '-';
    const mag = Math.abs(amount);
    const prefix = operationTypeValue === 'HONORED' ? '- ' : '';
    return `${prefix}USD ${formatDecimal(mag)}`;
  };

  const getOperationTypeLabel = (op: string) => {
    switch (op) {
      case 'CONFRONTED':
        return 'Afronte';
      case 'HONORED':
        return 'Honra';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return op || '-';
    }
  };

  const getOperationTypeVariant = (op: string): 'secondary-2' | 'success' | 'neutral' => {
    switch (op) {
      case 'CONFRONTED':
        return 'secondary-2';
      case 'HONORED':
        return 'success';
      default:
        return 'neutral';
    }
  };

  const operationTypeTitle =
    OPERATION_TYPE_OPTIONS.find((o) => o.value === operationType)?.label || 'Todas';

  const columns = [
    {
      sortable: true,
      name: renderSortableHeader('Operación', 'operation_type'),
      sortField: 'operation_type',
      selector: (row: InstallmentConfrontation) => (
        <P variant={getOperationTypeVariant(row.operation_type)} type="3" weight="bold" withMargin={false}>
          {getOperationTypeLabel(row.operation_type)}
        </P>
      ),
      minWidth: '100px',
      grow: 1,
    },
    {
      sortable: true,
      name: renderSortableHeader('Nº Aval', 'global_id'),
      sortField: 'global_id',
      selector: (row: InstallmentConfrontation) => (
        <P variant={getOperationTypeVariant(row.operation_type)} type="3" weight="regular" withMargin={false}>
          {row.global_id || '-'}
        </P>
      ),
      minWidth: '100px',
      grow: 1,
    },
    {
      sortable: true,
      name: renderSortableHeader('Tipo', 'monetization_type_name'),
      sortField: 'monetization_type_name',
      selector: (row: InstallmentConfrontation) => (
        <P variant={getOperationTypeVariant(row.operation_type)} type="3" weight="regular" withMargin={false}>
          {row.monetization_type_name || '-'}
        </P>
      ),
      minWidth: '120px',
      grow: 1,
    },
    {
      sortable: true,
      name: renderSortableHeader('Nº de aporte', 'contribution_id'),
      sortField: 'contribution_id',
      selector: (row: InstallmentConfrontation) => (
        <P variant={getOperationTypeVariant(row.operation_type)} type="3" weight="regular" withMargin={false}>
          {row.contribution_id != null ? row.contribution_id : '-'}
        </P>
      ),
      minWidth: '110px',
      grow: 1,
    },
    {
      sortable: true,
      name: renderSortableHeader('Nº cuota', 'installment_number'),
      sortField: 'installment_number',
      selector: (row: InstallmentConfrontation) => (
        <P variant={getOperationTypeVariant(row.operation_type)} type="3" weight="regular" withMargin={false}>
          {row.installment_number || '-'}
        </P>
      ),
      minWidth: '80px',
      grow: 0.8,
    },
    {
      sortable: true,
      name: renderSortableHeader('Divisas', 'currency_code'),
      sortField: 'currency_code',
      selector: (row: InstallmentConfrontation) => (
        <P variant={getOperationTypeVariant(row.operation_type)} type="3" weight="regular" withMargin={false}>
          {row.currency_code || '-'}
        </P>
      ),
      minWidth: '70px',
      grow: 0.6,
    },
    {
      sortable: true,
      name: renderSortableHeader(`Monto ${localCurrencyCode}`, 'amount_ars'),
      sortField: 'amount_ars',
      selector: (row: InstallmentConfrontation) => (
        <P variant={getOperationTypeVariant(row.operation_type)} modifier='regular' type="3" weight="regular" withMargin={false}>
          {formatHonoredRowArs(row.amount_ars, row.operation_type)}
        </P>
      ),
      minWidth: '130px',
      grow: 1,
    },
    {
      sortable: true,
      name: renderSortableHeader('Monto USD', 'amount_usd'),
      sortField: 'amount_usd',
      selector: (row: InstallmentConfrontation) => (
        <P variant={getOperationTypeVariant(row.operation_type)} modifier='regular' type="3" weight="regular" withMargin={false}>
          {formatHonoredRowUsd(row.amount_usd, row.operation_type)}
        </P>
      ),
      minWidth: '130px',
      grow: 1,
    },
    {
      sortable: true,
      name: renderSortableHeader('Fecha', 'confrontation_date'),
      sortField: 'confrontation_date',
      selector: (row: InstallmentConfrontation) => (
        <P variant={getOperationTypeVariant(row.operation_type)} type="3" weight="regular" withMargin={false}>
          {formatDate(row.confrontation_date)}
        </P>
      ),
      minWidth: '120px',
      grow: 1,
    },
    {
      sortable: true,
      name: renderSortableHeader('% de afronte / honra', 'percentage', { reserveSortGap: true }),
      sortField: 'percentage',
      selector: (row: InstallmentConfrontation) => (
        <P variant={getOperationTypeVariant(row.operation_type)} type="3" weight="regular" withMargin={false}>
          {formatPercentage(row.percentage)}
        </P>
      ),
      minWidth: '208px',
      allowOverflow: true,
      grow: 1,
    },
  ];

  const cardTotals = totals ?? emptyTotals();

  const totalCards = [
    {
      label: 'Total afrontado',
      ars: formatCardArs(cardTotals.total_afrontado_ars),
      usd: formatCardUsd(cardTotals.total_afrontado_usd),
      tone: 'default' as const,
    },
    {
      label: 'Total honrado',
      ars: formatCardArs(cardTotals.total_honrado_ars),
      usd: formatCardUsd(cardTotals.total_honrado_usd),
      tone: 'success' as const,
    },
    {
      label: 'Total neto',
      ars: formatCardArs(cardTotals.neto_ars),
      usd: formatCardUsd(cardTotals.neto_usd),
      tone: 'default' as const,
    },
  ];

  return (
    <>
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          Movimientos de afrontes y honras
        </H>
      </Card>

      <TotalsRow>
        {totalCards.map((card) => {
          const isHonrado = card.tone === 'success';
          const amountVariant = isHonrado ? 'success' : 'secondary-2';
          const amountModifier = isHonrado ? 'regular' : 'strong';
          const labelVariant = isHonrado ? 'success' : 'neutral';
          return (
            <Card key={card.label} variant='neutral' modifier='regular' spacing='3'>
              <TotalCardInner>
                <TotalAmountsRow>
                  <H
                    withMargin={false}
                    type='3'
                    weight='semibold'
                    modifier={amountModifier}
                    variant={amountVariant}
                  >
                    {card.ars}
                  </H>
                  <H
                    withMargin={false}
                    type='3'
                    weight='semibold'
                    modifier={amountModifier}
                    variant={amountVariant}
                  >
                    {card.usd}
                  </H>
                </TotalAmountsRow>
                <P
                  variant={labelVariant}
                  modifier={isHonrado ? 'regular' : 'strong'}
                  type='1'
                  withMargin={false}
                >
                  {card.label}
                </P>
              </TotalCardInner>
            </Card>
          );
        })}
      </TotalsRow>

      <FiltersRow>
        <FiltersGrid>
          <FilterField>
            <P
              variant='neutral'
              modifier='regular'
              type='3'
              weight='regular'
              withMargin={false}
              style={{ color: theme.colors.neutral.text.regular }}
            >
              Tipo de operación
            </P>
            <DropDownControl>
              <DropDown
                id='confrontation-operation-type'
                title={operationTypeTitle}
                size='md'
                marginTop='0'
                items={OPERATION_TYPE_OPTIONS.map((o) => ({ label: o.label }))}
                onChange={(label) => {
                  const opt = OPERATION_TYPE_OPTIONS.find((o) => o.label === label);
                  setOperationType(opt?.value ?? '');
                  resetToFirstPage();
                }}
              />
            </DropDownControl>
          </FilterField>
          <FilterField>
            <P
              variant='neutral'
              modifier='regular'
              type='3'
              weight='regular'
              withMargin={false}
              style={{ color: theme.colors.neutral.text.regular }}
            >
              Nº de aval
            </P>
            <InputControl>
              <Input
                id='confrontation-global-id'
                type='text'
                placeholder='Buscar por número de aval'
                value={globalIdInput}
                onChange={(e: any) => {
                  const value = e?.target?.value ?? e ?? '';
                  setGlobalIdInput(String(value));
                }}
              />
            </InputControl>
          </FilterField>
          <FilterField>
            <P
              variant='neutral'
              modifier='regular'
              type='3'
              weight='regular'
              withMargin={false}
              style={{ color: theme.colors.neutral.text.regular }}
            >
              Tipo de movimiento
            </P>
            <MultiSelectControl>
              <MultiSelectDropdown
                options={MOVEMENT_TYPE_OPTIONS}
                selectedValues={monetizationTypeNames}
                onChange={(values) => {
                  setMonetizationTypeNames(values);
                  resetToFirstPage();
                }}
                placeholder='Todas'
              />
            </MultiSelectControl>
          </FilterField>
          <FilterField>
            <P
              variant='neutral'
              modifier='regular'
              type='3'
              weight='regular'
              withMargin={false}
              style={{ color: theme.colors.neutral.text.regular }}
            >
              Nº de aporte
            </P>
            <MultiSelectControl>
              <MultiSelectDropdown
                options={contributionOptions}
                selectedValues={contributionIds}
                onChange={(values) => {
                  setContributionIds(values);
                  resetToFirstPage();
                }}
                placeholder='Todos'
              />
            </MultiSelectControl>
          </FilterField>
        </FiltersGrid>
        <FilterActions>
          <Tooltip position='top' msg='Limpiar filtros'>
            <button
              type='button'
              aria-label='Limpiar filtros'
              onClick={handleClearFilters}
              disabled={!isDirty}
            >
              <Icon
                name='delete_outline'
                variant={isDirty ? 'danger' : 'neutral'}
                width={24}
                height={24}
                cursor='pointer'
              />
            </button>
          </Tooltip>
          <Tooltip position='top' msg='Descargar CSV'>
            <button
              type='button'
              aria-label='Descargar CSV'
              onClick={handleExportCsv}
              disabled={pagination.totalElements === 0 || isExporting}
            >
              {isExporting ? (
                <Spinner size='sm' variant='primary' />
              ) : (
                <Icon name='download_outline' width={24} height={24} cursor='pointer' variant='info' />
              )}
            </button>
          </Tooltip>
        </FilterActions>
      </FiltersRow>

      {error ? (
        <Card>
          <P variant='neutral' modifier='weak' withMargin={false}>
            {error}
          </P>
        </Card>
      ) : loading && confrontations.length === 0 ? (
        <Card>
          <TableSkeleton rows={5} columns={10} />
        </Card>
      ) : (
        <Card
          style={{
            opacity: loading ? 0.55 : 1,
            transition: 'opacity 120ms ease',
          }}
        >
          <Table
            columns={columns}
            data={confrontations}
            tableData={pagination}
            handleChangePage={handleChangePage}
            onSort={handleSort}
            hideNativeSortIcon
            persistTableHead
            noDataComponent={
              <EmptyState>
                <P variant='neutral' modifier='weak' type='2' withMargin={false}>
                  {hasActiveFilters
                    ? 'No hay movimientos que coincidan con los filtros aplicados.'
                    : 'No hay movimientos de afrontes y honras para mostrar.'}
                </P>
                {isDirty ? (
                  <ClearFiltersLink type='button' onClick={handleClearFilters}>
                    Limpiar filtros
                  </ClearFiltersLink>
                ) : null}
              </EmptyState>
            }
            customStyles={{
              cells: {
                style: {
                  cursor: 'default',
                },
              },
            }}
          />
        </Card>
      )}
    </>
  );
};

export default ProtectorDetailsDefaultSection;
